import { publicUrl } from "../data/paths";

/** An outline icon on Lucide's 24-unit grid (lucide.dev, ISC licence). */
const lucide = (paths: string) =>
  `<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"` +
  ` stroke-width="2" stroke-linecap="round" stroke-linejoin="round"` +
  ` aria-hidden="true" focusable="false">${paths}</svg>`;

/** Inline marks for links and headings, keyed by their data-icon value. */
const ICONS: Record<string, string> = {
  // GitHub's own mark rather than a Lucide glyph, because it is the brand.
  github: `<svg class="icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.6 7.6 0 0 1 2-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z"/></svg>`,
  // user
  profile: lucide(
    `<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>`,
  ),
  // graduation-cap
  graduation: lucide(
    `<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/><path d="M22 10v6"/><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>`,
  ),
  // book-open
  book: lucide(
    `<path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/>`,
  ),
  // code
  code: lucide(`<path d="m16 18 6-6-6-6"/><path d="m8 6-6 6 6 6"/>`),
  // file-text
  file: lucide(
    `<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>`,
  ),
};

export const LESSONS = [
  ["01-world", "The world", "A maze and a partial view"],
  ["02-decision", "Partial observability", "Why history matters"],
  ["03-oracle", "Expert labels", "Learning from expert advice"],
  [
    "04-learning",
    "Learning from examples",
    "Imitation and reinforcement learning",
  ],
  ["05-architecture", "The network", "From observations to actions"],
  ["06-shift", "Errors and recovery", "Where to collect new labels"],
  ["07-measurement", "Measuring the effect", "How to make a fair comparison"],
] as const;

export function markActiveNav(): void {
  const here = window.location.pathname.replace(/index\.html$/, "");
  for (const link of document.querySelectorAll<HTMLAnchorElement>(
    ".site-nav a",
  )) {
    const target = new URL(link.href).pathname.replace(/index\.html$/, "");
    const isLesson =
      document.body.dataset.track === "lessons" &&
      target.endsWith("/journey/01-world/");
    if (
      isLesson ||
      target === here ||
      (target !== "/" && here.startsWith(target))
    ) {
      link.setAttribute("aria-current", "page");
    }
  }
  mountReadingNav(); // builds the rail, whose links also carry data-icon
  decorateChrome();
}

/** Every element that declares data-icon gets its mark. Runs once per page;
 *  calling it again changes nothing. */
export function decorateChrome(): void {
  for (const link of document.querySelectorAll<HTMLElement>("[data-icon]")) {
    const mark = ICONS[link.dataset.icon ?? ""];
    if (mark && !link.querySelector(".icon")) {
      link.insertAdjacentHTML("afterbegin", mark);
    }
  }
}

/** The way out of the current track, wearing that track's own color and mark,
 *  the same pair the site nav uses. Reproduce belongs to neither track, so it
 *  offers both. */
const CROSS = {
  study: {
    href: "study/",
    label: "Read the study",
    icon: "graduation",
  },
  lessons: {
    href: "journey/01-world/",
    label: "Explore the lessons",
    icon: "book",
  },
} as const;

function crossLinks(isLesson: boolean, page: string | undefined): string[] {
  const wanted: (keyof typeof CROSS)[] = isLesson
    ? ["study"]
    : page === "reproduce"
      ? ["study", "lessons"]
      : ["lessons"];
  return wanted.map((key) => {
    const { href, label, icon } = CROSS[key];
    return `<a class="reading-menu__other" data-nav="${key}" data-icon="${icon}" href="${publicUrl(href)}">${label}<span class="reading-menu__arrow" aria-hidden="true">↗</span></a>`;
  });
}

/** One navigation tree becomes a sticky sidebar or a compact contents menu. */
export function mountReadingNav(): void {
  const wrap = document.querySelector<HTMLElement>("main > .wrap");
  const article = wrap?.querySelector<HTMLElement>("article");
  if (!wrap || !article || wrap.querySelector(".reading-rail")) return;
  const page = document.body.dataset.page;
  const lessonIndex = LESSONS.findIndex(([id]) => id === page);
  const isLesson = lessonIndex >= 0;
  const sections = [...article.querySelectorAll<HTMLHeadingElement>("h2")];
  sections.forEach((heading, index) => {
    heading.id ||= `section-${index + 1}`;
  });
  wrap.classList.add("reading-layout");
  const rail = document.createElement("aside");
  rail.className = "reading-rail";
  const details = document.createElement("details");
  details.className = "reading-menu";
  const compact = window.matchMedia?.("(max-width: 1100px)");
  details.open = !compact?.matches;
  const heading = isLesson
    ? `Lesson ${lessonIndex + 1} of ${LESSONS.length}`
    : "Study contents";
  const title = isLesson
    ? LESSONS[lessonIndex]![1]
    : page === "reproduce"
      ? "Reproduce the study"
      : "Recovery Policy Learning";
  details.innerHTML = `
    <summary><span class="reading-menu__label">${heading}</span>
      <span class="reading-menu__title">${title}</span><span class="reading-menu__toggle" aria-hidden="true">⌄</span></summary>
    <div class="reading-menu__body">
      ${
        isLesson
          ? `<nav aria-label="Lessons"><ol class="lesson-list">${LESSONS.map(
              ([id, name], index) => `
        <li><a href="${publicUrl(`journey/${id}/`)}" ${index === lessonIndex ? 'aria-current="page"' : ""}>
          <span class="lesson-list__number">${String(index + 1).padStart(2, "0")}</span><span>${name}</span></a></li>`,
            ).join("")}</ol></nav>`
          : ""
      }
      <nav class="section-nav" aria-label="On this page"><p class="section-nav__title">${isLesson ? "In this lesson" : "On this page"}</p></nav>
      ${crossLinks(isLesson, page).join("")}
      <p class="reading-menu__note">${isLesson ? "The lessons explain the ideas. The study compares the two ways of using expert labels." : "For a step-by-step explanation of the ideas, explore the lessons."}</p>
    </div>`;
  const sectionNav = details.querySelector<HTMLElement>(".section-nav")!;
  for (const heading of sections) {
    const link = document.createElement("a");
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent;
    sectionNav.append(link);
  }
  rail.append(details);
  wrap.prepend(rail);
  const links = [...sectionNav.querySelectorAll("a")];
  function updateSection() {
    const threshold = compact?.matches ? 160 : 100;
    const current =
      sections
        .filter((h) => h.getBoundingClientRect().top <= threshold)
        .at(-1) ?? sections[0];
    links.forEach((link, i) => {
      if (sections[i] === current)
        link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
    });
  }
  let queued = false;
  window.addEventListener(
    "scroll",
    () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        updateSection();
        queued = false;
      });
    },
    { passive: true },
  );
  details.addEventListener("click", (event) => {
    if (compact?.matches && (event.target as HTMLElement).closest("a"))
      details.open = false;
  });
  details.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && compact?.matches) {
      details.open = false;
      details.querySelector("summary")?.focus();
    }
  });
  compact?.addEventListener("change", () => {
    details.open = !compact.matches;
    updateSection();
  });
  updateSection();
}
